// The Debug Panel (live-test instrumentation). A bottom-docked, collapsible
// drawer that makes the otherwise-invisible inner workings visible: a unified
// event timeline (left) merging the browser client's events with the gateway's
// reflex stream (drained over SSE), live stats (right top), and live parameter
// knobs (right bottom) for both the gateway reflex and the browser segmenter /
// forward cadence — so parameters can be tuned mid-session to find best-fit.
//
// Observability only: it reads the session store + the debugBus and talks to
// /bridge/debug (SSE) and /bridge/config (GET/POST). Gated by a persisted toggle
// (debugParams.isDebugPanelEnabled), mounted in Session's LiveRun.

import { LevelMeter } from '@/components/shell/LevelMeter'
import type { DebugEvent, ReflexParams } from '@/lib/bridge/protocol'
import {
  emitPanelEvent,
  getPanelEvents,
  subscribePanel,
  type PanelEvent
} from '@/lib/debug/debugBus'
import {
  DEBUG_PARAM_DEFAULTS,
  getBrowserDebugParams,
  setBrowserDebugParams,
  type BrowserDebugParams
} from '@/lib/debug/debugParams'
import type { SessionController } from '@/lib/session/sessionController'
import { useConnection, useLevel } from '@/lib/session/useSession'
import { cn } from '@/lib/utils'
import { Bug, ChevronDown, ChevronUp, RotateCcw, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

// Per-kind accent for the event log (tokens only — no raw hex).
const KIND_COLOR: Record<string, string> = {
  transcript: 'text-muted-foreground',
  classify: 'text-text',
  verdict: 'text-accent',
  delegate: 'text-warn',
  signal: 'text-success',
  forward: 'text-muted-foreground',
  tail: 'text-warn',
  capture: 'text-muted-foreground',
  params: 'text-accent',
  lifecycle: 'text-muted-foreground',
  wait: 'text-muted-foreground',
  error: 'text-danger'
}

function clockOf(t: number): string {
  const d = new Date(t)
  return d.toLocaleTimeString([], { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0')
}

export function DebugPanel({
  controller,
  onDisable
}: {
  controller: SessionController
  onDisable: () => void
}) {
  const { store, bridge } = controller
  const [expanded, setExpanded] = useState(false)
  const events = useSyncExternalStore(subscribePanel, getPanelEvents)
  const level = useLevel(store)
  const connection = useConnection(store)
  const [now, setNow] = useState(() => Date.now())
  const [browserParams, setBrowserParamsState] = useState<BrowserDebugParams>(
    getBrowserDebugParams
  )
  const [gatewayParams, setGatewayParams] = useState<ReflexParams | null>(null)
  const [gatewayReachable, setGatewayReachable] = useState(false)

  // A slow clock so "idle 1.2s" and rates stay live without thrashing.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(t)
  }, [])

  // Gateway side: read the live reflex params once, then stream debug events
  // into the shared bus (so the timeline interleaves them with browser events).
  useEffect(() => {
    if (!bridge?.token) return
    const { base, token } = bridge
    const q = `token=${encodeURIComponent(token)}`
    let cancelled = false
    fetch(`${base}/bridge/config?${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { params: ReflexParams | null } | null) => {
        if (cancelled) return
        setGatewayReachable(true)
        if (d?.params) setGatewayParams(d.params)
      })
      .catch(() => { })

    const es = new EventSource(`${base}/bridge/debug?since=0&${q}`)
    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(String(e.data)) as DebugEvent
        emitPanelEvent('gateway', ev.kind, ev.message, ev.data)
        if (ev.kind === 'params' && ev.data?.params) {
          setGatewayParams(ev.data.params as ReflexParams)
        }
      } catch {
        // ignore keep-alives / non-JSON frames
      }
    }
    return () => {
      cancelled = true
      es.close()
    }
  }, [bridge])

  // --- derived stats --------------------------------------------------------
  const stats = useMemo(() => {
    let classify = 0
    let verdicts = 0
    let acts = 0
    let signals = 0
    let forwards = 0
    let lastLatency: number | null = null
    let lastCacheRead: number | null = null
    let lastVerdict = ''
    let lastActivity = 0
    for (const e of events) {
      if (e.kind === 'classify') classify++
      else if (e.kind === 'verdict') {
        verdicts++
        if (!e.message.startsWith('silent')) acts++
        lastVerdict = e.message
        if (typeof e.data?.latencyMs === 'number') lastLatency = e.data.latencyMs
        if (typeof e.data?.cacheRead === 'number') lastCacheRead = e.data.cacheRead
      } else if (e.kind === 'signal') signals++
      else if (e.kind === 'forward') forwards++
      if (e.kind === 'transcript' || e.kind === 'signal' || e.kind === 'verdict') {
        if (e.t > lastActivity) lastActivity = e.t
      }
    }
    return {
      classify,
      verdicts,
      acts,
      signals,
      forwards,
      lastLatency,
      lastCacheRead,
      lastVerdict,
      idleMs: lastActivity ? now - lastActivity : null
    }
  }, [events, now])

  // --- param appliers -------------------------------------------------------
  function applyBrowser(partial: Partial<BrowserDebugParams>): void {
    const next = setBrowserDebugParams(partial)
    setBrowserParamsState(next)
    controller.setSegmentation({
      pauseMs: next.pauseMs,
      minChars: next.minChars,
      maxChars: next.maxChars,
      maxDurationMs: next.maxDurationMs
    })
    controller.ai?.setParams({ flushMs: next.flushMs, tailIdleMs: next.tailIdleMs })
  }

  function applyGateway(partial: Partial<ReflexParams>): void {
    if (!bridge?.token || !gatewayParams) return
    setGatewayParams({ ...gatewayParams, ...partial })
    fetch(`${bridge.base}/bridge/config?token=${encodeURIComponent(bridge.token)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-bridge-token': bridge.token },
      body: JSON.stringify(partial)
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { params: ReflexParams | null } | null) => {
        if (d?.params) setGatewayParams(d.params)
      })
      .catch(() => { })
  }

  return (
    <div className="shrink-0 border-t border-border bg-background shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
      {/* strip — always visible when the panel is enabled */}
      <div className="flex h-9 items-center gap-3 px-3 text-label">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 font-medium text-text hover:text-accent"
        >
          <Bug className="size-3.5" />
          <span>Debug</span>
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronUp className="size-3.5" />}
        </button>
        <span className="w-20">
          <LevelMeter level={level} />
        </span>
        <Stat label="conn" value={connection} />
        <Stat label="idle" value={stats.idleMs == null ? '—' : `${(stats.idleMs / 1000).toFixed(1)}s`} />
        <Stat label="classify" value={String(stats.classify)} />
        <Stat label="acts" value={`${stats.acts}/${stats.verdicts}`} />
        <span className="truncate text-muted-foreground">
          {stats.lastVerdict ? `verdict: ${stats.lastVerdict}` : 'no verdict yet'}
        </span>
        <button
          type="button"
          onClick={onDisable}
          title="Hide the debug panel"
          className="ml-auto grid size-5 place-items-center rounded text-muted-foreground/70 hover:text-danger"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="grid h-[40vh] grid-cols-[1fr_22rem] border-t border-border">
          <EventLog events={events} />
          <div className="flex flex-col overflow-y-auto border-l border-border">
            <StatsGrid stats={stats} gatewayReachable={gatewayReachable} />
            <ParamControls
              browser={browserParams}
              gateway={gatewayParams}
              onBrowser={applyBrowser}
              onGateway={applyGateway}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// --- event log --------------------------------------------------------------

function EventLog({ events }: { events: PanelEvent[] }) {
  const ref = useRef<HTMLDivElement>(null)
  // Newest first — the live edge stays at the top, no autoscroll needed.
  const rows = useMemo(() => [...events].reverse(), [events])
  return (
    <div ref={ref} className="overflow-y-auto p-2 font-mono text-[11px] leading-relaxed">
      {rows.length === 0 ? (
        <p className="text-muted-foreground">No events yet — start talking (or run the dev feeder).</p>
      ) : (
        rows.map((e) => (
          <div key={e.id} className="flex gap-2">
            <span className="shrink-0 text-muted-foreground/60">{clockOf(e.t)}</span>
            <span
              className={cn(
                'min-w-[6rem] shrink-0',
                e.source === 'gateway' ? 'text-accent/70' : 'text-muted-foreground/70'
              )}
            >
              {e.source === 'gateway' ? 'gw' : 'web'}·{e.kind}
            </span>
            <span className={cn('min-w-0 break-words', KIND_COLOR[e.kind] ?? 'text-text')}>
              {e.message}
            </span>
          </div>
        ))
      )}
    </div>
  )
}

// --- stats ------------------------------------------------------------------

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-center gap-1 text-muted-foreground">
      <span className="text-muted-foreground/60">{label}</span>
      <span className="text-text">{value}</span>
    </span>
  )
}

function StatsGrid({
  stats,
  gatewayReachable
}: {
  stats: {
    classify: number
    verdicts: number
    acts: number
    signals: number
    forwards: number
    lastLatency: number | null
    lastCacheRead: number | null
  }
  gatewayReachable: boolean
}) {
  return (
    <div className="border-b border-border p-3">
      <h3 className="mb-2 text-label font-medium uppercase tracking-wide text-muted-foreground">
        Stats
      </h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-label">
        <Row k="gateway" v={gatewayReachable ? 'reachable' : 'unreachable'} />
        <Row k="classifies" v={String(stats.classify)} />
        <Row k="verdicts" v={String(stats.verdicts)} />
        <Row k="acts" v={String(stats.acts)} />
        <Row k="signals" v={String(stats.signals)} />
        <Row k="flushes" v={String(stats.forwards)} />
        <Row k="last latency" v={stats.lastLatency == null ? '—' : `${stats.lastLatency}ms`} />
        <Row k="last cache rd" v={stats.lastCacheRead == null ? '—' : String(stats.lastCacheRead)} />
      </div>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground/70">{k}</span>
      <span className="text-text">{v}</span>
    </div>
  )
}

// --- param knobs ------------------------------------------------------------

function ParamControls({
  browser,
  gateway,
  onBrowser,
  onGateway
}: {
  browser: BrowserDebugParams
  gateway: ReflexParams | null
  onBrowser: (p: Partial<BrowserDebugParams>) => void
  onGateway: (p: Partial<ReflexParams>) => void
}) {
  return (
    <div className="space-y-4 p-3">
      <ParamGroup
        title="Gateway reflex"
        subtitle={gateway ? gateway.model : 'no reflex (no key)'}
        onReset={
          gateway
            ? () =>
              onGateway({
                debounceMs: 500,
                minIntervalMs: 1500,
                windowLines: 40,
                maxTokens: 400
              })
            : undefined
        }
      >
        <Knob label="debounce" unit="ms" min={0} max={3000} step={50} value={gateway?.debounceMs ?? 0} disabled={!gateway} onChange={(v) => onGateway({ debounceMs: v })} />
        <Knob label="min interval" unit="ms" min={0} max={8000} step={100} value={gateway?.minIntervalMs ?? 0} disabled={!gateway} onChange={(v) => onGateway({ minIntervalMs: v })} />
        <Knob label="window" unit="lines" min={1} max={120} step={1} value={gateway?.windowLines ?? 0} disabled={!gateway} onChange={(v) => onGateway({ windowLines: v })} />
        <Knob label="max tokens" unit="" min={1} max={2000} step={50} value={gateway?.maxTokens ?? 0} disabled={!gateway} onChange={(v) => onGateway({ maxTokens: v })} />
      </ParamGroup>

      <ParamGroup
        title="Browser segmenter"
        subtitle="F10 thresholds"
        onReset={() =>
          onBrowser({
            pauseMs: DEBUG_PARAM_DEFAULTS.pauseMs,
            minChars: DEBUG_PARAM_DEFAULTS.minChars,
            maxChars: DEBUG_PARAM_DEFAULTS.maxChars,
            maxDurationMs: DEBUG_PARAM_DEFAULTS.maxDurationMs
          })
        }
      >
        <Knob label="pause" unit="ms" min={500} max={8000} step={100} value={browser.pauseMs} onChange={(v) => onBrowser({ pauseMs: v })} />
        <Knob label="min chars" unit="" min={0} max={400} step={10} value={browser.minChars} onChange={(v) => onBrowser({ minChars: v })} />
        <Knob label="max chars" unit="" min={80} max={800} step={20} value={browser.maxChars} onChange={(v) => onBrowser({ maxChars: v })} />
        <Knob label="max duration" unit="ms" min={5000} max={60000} step={1000} value={browser.maxDurationMs} onChange={(v) => onBrowser({ maxDurationMs: v })} />
      </ParamGroup>

      <ParamGroup
        title="Browser forwarding"
        subtitle="feed cadence"
        onReset={() =>
          onBrowser({
            flushMs: DEBUG_PARAM_DEFAULTS.flushMs,
            tailIdleMs: DEBUG_PARAM_DEFAULTS.tailIdleMs
          })
        }
      >
        <Knob label="flush" unit="ms" min={50} max={3000} step={50} value={browser.flushMs} onChange={(v) => onBrowser({ flushMs: v })} />
        <Knob label="tail idle" unit="ms" min={500} max={8000} step={100} value={browser.tailIdleMs} onChange={(v) => onBrowser({ tailIdleMs: v })} />
      </ParamGroup>
    </div>
  )
}

function ParamGroup({
  title,
  subtitle,
  onReset,
  children
}: {
  title: string
  subtitle: string
  onReset?: () => void
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <h3 className="text-label font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        <span className="text-label text-muted-foreground/50">{subtitle}</span>
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            title="Reset to defaults"
            className="ml-auto grid size-4 place-items-center rounded text-muted-foreground/60 hover:text-accent"
          >
            <RotateCcw className="size-3" />
          </button>
        )}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  )
}

function Knob({
  label,
  unit,
  min,
  max,
  step,
  value,
  disabled,
  onChange
}: {
  label: string
  unit: string
  min: number
  max: number
  step: number
  value: number
  disabled?: boolean
  onChange: (v: number) => void
}) {
  return (
    <label className={cn('flex items-center gap-2 text-label', disabled && 'opacity-40')}>
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 flex-1 accent-accent"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-6 w-16 rounded border border-border bg-background px-1 text-right text-text"
      />
      <span className="w-8 shrink-0 text-muted-foreground/50">{unit}</span>
    </label>
  )
}
